import { superAdminRouterKeys } from './superAdmin'
import { client } from 'src/enviroment/api/axios'
import { logMetricsEvent, MetricsEvent } from 'src/enviroment/api/metrics'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import pLimit from 'p-limit'
import { Mutation, Query } from '../index'
import {
  CreateProjectRequestData,
  DataRoomDocumentsResponse,
  DataRoomFolder,
  DataRoomFoldersResponse,
  DataRoomStorageUsage,
  DataRoomUpload,
  DataRoomUploadResult,
  DeleteManyProjectsResponse,
  Project,
  ProjectNames,
  ProjectStatus,
  ProjectValidationResult,
} from 'src/types/projects'
import { LayerUploadStatus } from 'src/types/layer'
import { useTenantStore } from 'src/enviroment/store/tenentStore'
import { queryTool } from 'src/enviroment/lib/utils'
import { getMimeType } from 'src/enviroment/lib/fileUtils'
import { handleApiError } from 'src/enviroment/api/errorHandler'
import { loadKnowledgeBase } from 'src/enviroment/api/chat/api'
import { ProjectUpdateFormData } from 'src/components/organisms/dialogs/EditMyProjectDialog'
import { CurrentUser } from 'src/types/user'
import { folderName } from 'src/enviroment/utils/folderName'
import { isInsideFolder } from 'src/enviroment/utils/isInsideFolder'
import { fileDisplayPath } from 'src/enviroment/utils/fileDisplayPath'

const DATA_ROOM_UPLOAD_CONCURRENCY = 4

export interface DataRoomUploadVariables {
  projectId: string
  uploads: DataRoomUpload[]
  folderPaths?: string[][]
  onProgress?: (done: number) => void
}

const isConflict = (error: unknown): boolean => error instanceof AxiosError && error.response?.status === 409

const isUnderFailedFolder = (path: string | null, failedFolders: { path: string }[]): boolean =>
  failedFolders.some((folder) => isInsideFolder(path, folder.path))

const createDataRoomFolder = (tenantId: string, projectId: string, folderPath: string) =>
  client.post<{ success: boolean }>(`/tenants/${tenantId}/projects/${projectId}/data-room/folders`, { folderPath })

/** Creates folders level by level, reusing existing ones; returns the folders that failed, whose subtrees are skipped. */
const createDataRoomFolderLevels = async (
  tenantId: string,
  projectId: string,
  levels: string[][],
): Promise<{ path: string; error: unknown }[]> => {
  const failed: { path: string; error: unknown }[] = []
  for (const level of levels) {
    const reachable = level.filter((path) => !isUnderFailedFolder(path, failed))
    const limit = pLimit(DATA_ROOM_UPLOAD_CONCURRENCY)
    const results = await Promise.allSettled(
      reachable.map((path) =>
        limit(() =>
          createDataRoomFolder(tenantId, projectId, path).catch((error) => {
            if (!isConflict(error)) throw error
          }),
        ),
      ),
    )
    results.forEach((result, index) => {
      if (result.status === 'rejected') failed.push({ path: reachable[index], error: result.reason })
    })
  }
  return failed
}

const uploadFileToDataRoom = async (tenantId: string, projectId: string, { file, folderPath }: DataRoomUpload) => {
  const { data } = await client.post<{ sasUrl: string }>(
    `/tenants/${tenantId}/projects/${projectId}/data-room/files:generate-upload-url`,
    { fileName: file.name, folderPath: folderPath ?? undefined },
  )
  const uploadRes = await fetch(data.sasUrl, {
    method: 'PUT',
    // the stored content-type is what the preview sas serves back, so an unrecognised file must not land as octet-stream
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': getMimeType(file) },
    body: file,
  })
  if (!uploadRes.ok) {
    throw new Error(`Upload failed for ${file.name}: ${uploadRes.status} ${uploadRes.statusText}`)
  }
  logMetricsEvent(MetricsEvent.DATA_ROOM_FILE_UPLOADED, {
    projectId,
    fileName: file.name,
    fileSize: file.size,
    ...(folderPath != null && { folderPath }),
  })
}

/** Uploads every file it can; a taken name (409) is skipped, not failed, so re-picking a folder merges into it. */
const uploadFilesToDataRoomFolder = async (
  tenantId: string,
  projectId: string,
  uploads: DataRoomUpload[],
  onSettled?: () => void,
): Promise<{
  uploaded: File[]
  alreadyExisting: { file: File; error: unknown }[]
  failed: { file: File; error: unknown }[]
}> => {
  const limit = pLimit(DATA_ROOM_UPLOAD_CONCURRENCY)
  const results = await Promise.allSettled(
    uploads.map((upload) => limit(() => uploadFileToDataRoom(tenantId, projectId, upload).finally(onSettled))),
  )
  const files = uploads.map((upload) => upload.file)
  const rejected = results.flatMap((result, index) =>
    result.status === 'rejected' ? [{ file: files[index], error: result.reason }] : [],
  )
  return {
    uploaded: files.filter((_, index) => results[index].status === 'fulfilled'),
    alreadyExisting: rejected.filter(({ error }) => isConflict(error)),
    failed: rejected.filter(({ error }) => !isConflict(error)),
  }
}

export interface ExportLayerDTO {
  export_link: string
}

export interface ExportLayerParams {
  projectId: string
  layerId: string
}

export interface CreateProjectFromShapeResponse {
  id: string
  name: string
  location: string
  area: number
  image: string
}

// Default number of items fetched per page for project-scoped cursor-paginated lists.
const PROJECTS_PAGE_SIZE = 20

export const projectRouterKeys = {
  list: 'project.list',
  atlas: 'project.atlas',
  names: 'project.names',
  detail: 'project.detail',
  status: 'project.status',
  dataRoomFolders: 'project.dataRoomFolders',
  dataRoomDocuments: 'project.dataRoomDocuments',
  dataRoomStorageUsage: 'project.dataRoomStorageUsage',
  dataRoomPreviewUrl: 'project.dataRoomPreviewUrl',
} as const

export const projectRouter = {
  atlas: {
    useQuery: (
      params: {
        typeIds?: string[]
        stageIds?: string[]
        location?: string[]
        includePublic?: boolean
        includeTenant?: boolean
      },
      opts?: Query<Project[]>,
    ) => {
      const { typeIds = [], stageIds = [], location = [], ...includeParams } = params
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useQuery<Project[]>({
        queryKey: [projectRouterKeys.atlas, selectedTenantId, typeIds, stageIds, location, includeParams],
        queryFn: async () => {
          const queryParams = {
            ...(typeIds.length > 0 && { typeIds }),
            ...(stageIds.length > 0 && { stageIds }),
            ...(location.length > 0 && { location }),
            ...(includeParams || {}),
          }
          return (await client.get<Project[]>(`/tenants/${selectedTenantId}/projects:atlas`, { params: queryParams }))
            .data
        },
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
        staleTime: 3 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        retry: 3,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
        enabled: Boolean(selectedTenantId),
        ...opts,
      })
    },
  },

  list: {
    useInfiniteQuery: (params: {
      searchTerm?: string
      typeIds?: string[]
      stageIds?: string[]
      location?: string[]
      includePublic?: boolean
      includeTenant?: boolean
    }) => {
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)
      const { searchTerm, typeIds = [], stageIds = [], location = [], ...includeParams } = params
      const take = PROJECTS_PAGE_SIZE

      return useInfiniteQuery({
        queryKey: [projectRouterKeys.list, selectedTenantId, searchTerm, typeIds, stageIds, location, includeParams],
        queryFn: async ({ pageParam }) => {
          return (
            await client.get<Project[]>(`/tenants/${selectedTenantId}/projects:filter`, {
              params: {
                cursorId: pageParam,
                take,
                query: queryTool(searchTerm),
                typeIds,
                stageIds,
                location,
                ...(includeParams || {}),
              },
            })
          ).data
        },
        enabled: Boolean(selectedTenantId),
        // Stop paginating once a page returns fewer items than requested (last page).
        getNextPageParam: (lastPage) => (lastPage.length < take ? undefined : lastPage.at(-1)?.id),
      })
    },
  },

  names: {
    useQuery: (opts?: Query<ProjectNames[]>) => {
      const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useQuery<ProjectNames[]>({
        queryKey: [projectRouterKeys.names],
        queryFn: async () => {
          return (await client.get<ProjectNames[]>(`/tenants/${currentTenantId}/projects:all-names`)).data
        },
        ...opts,
      })
    },
  },

  byId: {
    useQuery: (projectId: string, opts?: Query<Project>) => {
      return useQuery<Project>({
        queryKey: [projectRouterKeys.detail, projectId],
        queryFn: async () => {
          // Read at fetch time: an invalidate right after a tenant switch runs before the re-render.
          const currentTenantId = useTenantStore.getState().currentTenant?.id
          return (await client.get<Project>(`/tenants/${currentTenantId}/projects/${projectId}`)).data
        },
        ...opts,
      })
    },
  },

  exportLayer: {
    useMutation: (opts?: Mutation<ExportLayerParams, ExportLayerDTO>) => {
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useMutation({
        mutationFn: async ({ projectId, layerId }: ExportLayerParams) => {
          const response = await client.get<ExportLayerDTO>(
            `/tenants/${selectedTenantId}/projects/${projectId}/layers/${layerId}:export`,
          )
          window.open(response.data.export_link, '_blank')
          return response.data
        },
        ...opts,
      })
    },
  },

  /** @deprecated Use createWithDocuments. */
  createV2: {
    useMutation: (opts?: Mutation<CreateProjectRequestData, CreateProjectFromShapeResponse>) => {
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useMutation({
        mutationFn: async (data: CreateProjectRequestData) => {
          if (!selectedTenantId) {
            throw new Error('Tenant not selected')
          }

          const formData = new FormData()
          formData.append('name', data.name)
          formData.append('shape', data.shape)
          data.userContext && formData.append('userContext', data.userContext)

          return (
            await client.post<CreateProjectFromShapeResponse>(`/tenants/${selectedTenantId}/v2/projects`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            })
          ).data
        },
        ...opts,
        onError: (error, ...args) => {
          handleApiError(error)
          opts?.onError?.(error, ...args)
        },
      })
    },
  },

  /**
   * Creates a project against a client-generated id, uploading any staged data room documents first.
   * Documents are held client-side until submit, so abandoning the form leaves no orphaned blobs; on
   * failure the blobs are rolled back, since no project row exists to own them.
   */
  createWithDocuments: {
    useMutation: (
      opts?: Mutation<CreateProjectRequestData & { id: string; documents?: File[] }, CreateProjectFromShapeResponse>,
    ) => {
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async ({
          id: projectId,
          documents = [],
          ...data
        }: CreateProjectRequestData & { id: string; documents?: File[] }) => {
          if (!selectedTenantId) {
            throw new Error('Tenant not selected')
          }

          try {
            if (documents.length) {
              const totalBytes = documents.reduce((sum, file) => sum + file.size, 0)
              await client.post(`/tenants/${selectedTenantId}/data-room:validate-storage`, {
                fileSizeBytes: totalBytes,
              })
              const { uploaded, alreadyExisting, failed } = await uploadFilesToDataRoomFolder(
                selectedTenantId,
                projectId,
                documents.map((file) => ({ file, folderPath: null })),
              )
              // The catch below rolls the blobs back only if this throws
              if (uploaded.length < documents.length) {
                throw (failed[0] ?? alreadyExisting[0]).error
              }
            }

            const formData = new FormData()
            formData.append('id', projectId)
            formData.append('name', data.name)
            formData.append('shape', data.shape)
            data.userContext && formData.append('userContext', data.userContext)

            const project = (
              await client.post<CreateProjectFromShapeResponse>(`/tenants/${selectedTenantId}/v2/projects`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              })
            ).data

            // Deliberately not awaited: indexing is slow and the user proceeds without it.
            if (documents.length) {
              const currentUser = queryClient.getQueryData<CurrentUser>(['user.currentUser'])
              if (currentUser) {
                loadKnowledgeBase({ userId: currentUser.id, tenantId: selectedTenantId, projectId }).catch((error) =>
                  console.error('Failed to refresh COPA knowledge base:', error),
                )
              }
            }

            return project
          } catch (error) {
            // Nothing here outlives a failure: no project row exists yet, so drop any blobs already written.
            if (documents.length) {
              await client
                .delete(`/tenants/${selectedTenantId}/projects/${projectId}/data-room`)
                .catch((cleanupError) => console.error('Failed to discard project documents:', cleanupError))
            }
            throw error
          }
        },
        ...opts,
        onSuccess: (...args) => {
          queryClient.invalidateQueries({ queryKey: [projectRouterKeys.list] })
          opts?.onSuccess?.(...args)
        },
        onError: (error, ...args) => {
          if (opts?.onError) {
            opts.onError(error, ...args)
          } else {
            handleApiError(error)
          }
        },
      })
    },
  },

  validate: {
    useMutation: (opts?: Mutation<File, ProjectValidationResult>) => {
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useMutation({
        mutationFn: async (file: File): Promise<ProjectValidationResult> => {
          const formData = new FormData()
          formData.append('shape', file)
          return (
            await client.post<ProjectValidationResult>(`/tenants/${selectedTenantId}/projects:validate`, formData)
          ).data
        },
        ...opts,
        onError: (error, ...args) => {
          handleApiError(error)
          opts?.onError?.(error, ...args)
        },
      })
    },
  },

  update: {
    useMutation: (opts?: Mutation<{ projectId: string; payload: ProjectUpdateFormData }, void>) => {
      const queryClient = useQueryClient()
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useMutation({
        mutationFn: async ({ projectId, payload }: { projectId: string; payload: ProjectUpdateFormData }) => {
          await client.patch(`/tenants/${selectedTenantId}/projects/${projectId}`, payload)
        },
        ...opts,
        onError: (error, ...args) => {
          handleApiError(error)
          opts?.onError?.(error, ...args)
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({
            predicate: (query) => {
              const queryKey = query.queryKey as unknown[]
              return Array.isArray(queryKey) && queryKey[0] === projectRouterKeys.list
            },
          })
          queryClient.invalidateQueries({ queryKey: [projectRouterKeys.atlas] })
          opts?.onSuccess?.(...args)
        },
      })
    },
  },

  delete: {
    useMutation: (opts?: Mutation<string, unknown>) => {
      const queryClient = useQueryClient()
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useMutation({
        mutationFn: async (projectId: string) => {
          return (await client.delete(`/tenants/${selectedTenantId}/projects/${projectId}`)).data
        },
        ...opts,
        onError: (error, ...args) => {
          handleApiError(error)
          opts?.onError?.(error, ...args)
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({
            predicate: (query) => {
              const queryKey = query.queryKey as unknown[]
              return Array.isArray(queryKey) && queryKey[0] === projectRouterKeys.list
            },
          })
          queryClient.invalidateQueries({ queryKey: [projectRouterKeys.atlas] })
          const currentUser = queryClient.getQueryData<CurrentUser>(['user.currentUser'])
          if (currentUser?.isSuperadmin) {
            queryClient.invalidateQueries({ queryKey: [superAdminRouterKeys.globalProjectList] })
          }
          opts?.onSuccess?.(...args)
        },
      })
    },
  },

  deleteMany: {
    useMutation: (opts?: Mutation<string[], DeleteManyProjectsResponse>) => {
      const queryClient = useQueryClient()
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useMutation({
        mutationFn: async (projectIds: string[]) => {
          return (
            await client.delete<DeleteManyProjectsResponse>(`/tenants/${selectedTenantId}/projects`, {
              data: { projectIds },
            })
          ).data
        },
        ...opts,
        onError: (error, ...args) => {
          handleApiError(error)
          opts?.onError?.(error, ...args)
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({
            predicate: (query) => {
              const queryKey = query.queryKey as unknown[]
              return Array.isArray(queryKey) && queryKey[0] === projectRouterKeys.list
            },
          })
          queryClient.invalidateQueries({ queryKey: [projectRouterKeys.atlas] })
          const currentUser = queryClient.getQueryData<CurrentUser>(['user.currentUser'])
          if (currentUser?.isSuperadmin) {
            queryClient.invalidateQueries({ queryKey: [superAdminRouterKeys.globalProjectList] })
          }
          opts?.onSuccess?.(...args)
        },
      })
    },
  },

  status: {
    useQuery: (projectId: string, enabled = true, opts?: Query<ProjectStatus>) => {
      const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useQuery<ProjectStatus>({
        queryKey: [projectRouterKeys.status, projectId],
        queryFn: async () => {
          return (await client.get<ProjectStatus>(`/tenants/${currentTenantId}/projects/${projectId}/status`)).data
        },
        enabled: enabled && Boolean(currentTenantId),
        refetchInterval: (data: ProjectStatus | undefined) => {
          const POLL_INTERVAL_MS = 5000

          const layersAreUploaded = data?.layerUploadStatus === 'COMPLETED' || data?.layerUploadStatus === 'ERROR'
          const thumbnailIsGenerated = !!data?.thumbnailUrl
          // If there are no worker layers at all, stop polling once AOI is ready and thumbnail is generated.
          const noWorkerLayers = (data?.layers.length ?? 0) === 0
          const aoiOnlyDone = noWorkerLayers && (data?.aoiLayerReady ?? false) && thumbnailIsGenerated
          const copaSettled =
            data?.copaStatus === LayerUploadStatus.COMPLETED || data?.copaStatus === LayerUploadStatus.ERROR

          if (((layersAreUploaded && thumbnailIsGenerated) || aoiOnlyDone) && copaSettled) {
            return false
          }

          return POLL_INTERVAL_MS
        },
        ...opts,
      })
    },
  },

  dataRoom: {
    storageUsage: {
      useQuery: (opts?: Query<DataRoomStorageUsage>) => {
        const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

        return useQuery<DataRoomStorageUsage>({
          queryKey: [projectRouterKeys.dataRoomStorageUsage, currentTenantId],
          queryFn: async (): Promise<DataRoomStorageUsage> => {
            const { data } = await client.get(`/tenants/${currentTenantId}/data-room/storage-usage`)
            return data
          },
          ...opts,
          enabled: Boolean(currentTenantId) && (opts?.enabled ?? true),
        })
      },
    },

    folders: {
      useQuery: (
        projectId: string,
        folderPath?: string | null,
        take?: number,
        opts?: Query<DataRoomFoldersResponse>,
      ) => {
        const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

        return useQuery<DataRoomFoldersResponse>({
          queryKey: [projectRouterKeys.dataRoomFolders, projectId, folderPath ?? null, take],
          queryFn: async (): Promise<DataRoomFoldersResponse> => {
            try {
              const { data } = await client.get(`/tenants/${currentTenantId}/projects/${projectId}/data-room/folders`, {
                params: {
                  take: take ?? PROJECTS_PAGE_SIZE,
                  ...(folderPath != null && { folderPath }),
                },
              })
              return {
                data: data.data,
                documentCount: data.documentCount,
                fileCount: data.fileCount,
                nextCursor: data.nextCursor,
              }
            } catch (error: any) {
              if (error?.response?.status === 404) {
                return { data: [], documentCount: 0, fileCount: 0, nextCursor: null }
              }
              throw error
            }
          },
          enabled: Boolean(projectId) && Boolean(currentTenantId),
          retry: (failureCount, error: any) => (error?.response?.status === 404 ? false : failureCount < 3),
          ...opts,
        })
      },
    },

    documents: {
      useInfiniteQuery: (params: {
        projectId: string
        folderPath: string | null
        enabled?: boolean
        take?: number
      }) => {
        const { projectId, folderPath, enabled = true, take } = params
        const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

        return useInfiniteQuery<DataRoomDocumentsResponse>({
          queryKey: [projectRouterKeys.dataRoomDocuments, projectId, folderPath, take],
          queryFn: async ({ pageParam }): Promise<DataRoomDocumentsResponse> => {
            try {
              const { data } = await client.get(`/tenants/${currentTenantId}/projects/${projectId}/data-room/files`, {
                params: {
                  cursorId: pageParam,
                  take: take ?? PROJECTS_PAGE_SIZE,
                  ...(folderPath !== null && { folderPath }),
                },
              })
              return { documents: data.data, total: data.count ?? 0, nextCursor: data.nextCursor }
            } catch (error: any) {
              if (error?.response?.status === 404) {
                return { documents: [], total: 0, nextCursor: null }
              }
              throw error
            }
          },
          enabled: enabled && Boolean(projectId) && Boolean(currentTenantId),
          retry: (failureCount, error: any) => (error?.response?.status === 404 ? false : failureCount < 3),
          getNextPageParam: (lastPage) => lastPage.nextCursor,
        })
      },
    },

    preview: {
      useQuery: (projectId: string, documentId: string | null, opts?: Query<string>) => {
        const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

        return useQuery<string>({
          queryKey: [projectRouterKeys.dataRoomPreviewUrl, currentTenantId, projectId, documentId],
          queryFn: async (): Promise<string> => {
            const { data } = await client.get<{ previewUrl: string }>(
              `/tenants/${currentTenantId}/projects/${projectId}/data-room:preview`,
              { params: { documentId } },
            )
            return data.previewUrl
          },
          // each mint returns a new URL, so refetching would swap the iframe src and reload it;
          // 30min keeps it stable well inside the 60min SAS lifetime
          staleTime: 30 * 60 * 1000,
          refetchOnWindowFocus: false,
          ...opts,
          enabled: Boolean(currentTenantId) && Boolean(projectId) && Boolean(documentId) && (opts?.enabled ?? true),
        })
      },
    },

    download: {
      useMutation: (opts?: Mutation<{ projectId: string; documentId: string }, string>) => {
        const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

        return useMutation({
          mutationFn: async ({ projectId, documentId }: { projectId: string; documentId: string }) => {
            return (
              await client.get<{ downloadUrl: string }>(
                `/tenants/${currentTenantId}/projects/${projectId}/data-room:download`,
                { params: { documentId } },
              )
            ).data.downloadUrl
          },
          onSuccess: (downloadUrl, ...args) => {
            window.open(downloadUrl, '_blank')
            opts?.onSuccess?.(downloadUrl, ...args)
          },
          ...opts,
        })
      },
    },

    deleteFiles: {
      useMutation: (opts?: Mutation<{ projectId: string; documentIds: string[] }, void>) => {
        const currentTenantId = useTenantStore((state) => state.currentTenant?.id)
        const queryClient = useQueryClient()

        return useMutation({
          mutationFn: async ({ projectId, documentIds }: { projectId: string; documentIds: string[] }) => {
            const currentUser = queryClient.getQueryData<CurrentUser>(['user.currentUser'])
            if (!currentUser || !currentTenantId) {
              throw new Error('User not authenticated')
            }
            await client.delete(`/tenants/${currentTenantId}/projects/${projectId}/data-room/files`, {
              data: { documentIds },
            })
            await loadKnowledgeBase({ userId: currentUser.id, tenantId: currentTenantId, projectId })
          },
          ...opts,
          onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomDocuments] })
            queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomFolders] })
            queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomStorageUsage] })
            opts?.onSuccess?.(...args)
          },
          onError: (error, ...args) => {
            if (opts?.onError) {
              opts.onError(error, ...args)
            } else {
              handleApiError(error)
            }
          },
        })
      },
    },

    createFolder: {
      useMutation: (opts?: Mutation<{ projectId: string; folderPath: string }, DataRoomFolder>) => {
        const currentTenantId = useTenantStore((state) => state.currentTenant?.id)
        const queryClient = useQueryClient()

        return useMutation({
          mutationFn: async ({
            projectId,
            folderPath,
          }: {
            projectId: string
            folderPath: string
          }): Promise<DataRoomFolder> => {
            if (!currentTenantId) {
              throw new Error('Tenant not selected')
            }
            await createDataRoomFolder(currentTenantId, projectId, folderPath)
            return { id: folderPath, name: folderName(folderPath), path: folderPath, documentCount: 0 }
          },
          ...opts,
          onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomFolders] })
            opts?.onSuccess?.(...args)
          },
          onError: (error, ...args) => {
            if (opts?.onError) {
              opts.onError(error, ...args)
            } else {
              handleApiError(error)
            }
          },
        })
      },
    },

    deleteFolder: {
      useMutation: (
        opts?: Mutation<{ projectId: string; folderPath: string; hasFiles: boolean }, { success: boolean }>,
      ) => {
        const currentTenantId = useTenantStore((state) => state.currentTenant?.id)
        const queryClient = useQueryClient()

        return useMutation({
          mutationFn: async ({
            projectId,
            folderPath,
            hasFiles,
          }: {
            projectId: string
            folderPath: string
            hasFiles: boolean
          }) => {
            const result = (
              await client.delete<{ success: boolean }>(
                `/tenants/${currentTenantId}/projects/${projectId}/data-room/folders`,
                { params: { folderPath } },
              )
            ).data
            if (hasFiles) {
              const currentUser = queryClient.getQueryData<CurrentUser>(['user.currentUser'])
              if (currentUser && currentTenantId) {
                await loadKnowledgeBase({ userId: currentUser.id, tenantId: currentTenantId, projectId })
              }
            }
            return result
          },
          ...opts,
          onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomFolders] })
            queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomDocuments] })
            queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomStorageUsage] })
            opts?.onSuccess?.(...args)
          },
          onError: (error, ...args) => {
            if (opts?.onError) {
              opts.onError(error, ...args)
            } else {
              handleApiError(error)
            }
          },
        })
      },
    },

    renameFolder: {
      useMutation: (
        opts?: Mutation<{ projectId: string; folderPath: string; newFolderName: string }, { success: boolean }>,
      ) => {
        const currentTenantId = useTenantStore((state) => state.currentTenant?.id)
        const queryClient = useQueryClient()

        return useMutation({
          mutationFn: async ({
            projectId,
            folderPath,
            newFolderName,
          }: {
            projectId: string
            folderPath: string
            newFolderName: string
          }) => {
            const result = (
              await client.patch<{ success: boolean }>(
                `/tenants/${currentTenantId}/projects/${projectId}/data-room/folders`,
                { folderPath, newFolderName },
              )
            ).data
            const currentUser = queryClient.getQueryData<CurrentUser>(['user.currentUser'])
            if (currentUser && currentTenantId) {
              await loadKnowledgeBase({ userId: currentUser.id, tenantId: currentTenantId, projectId })
            }
            return result
          },
          ...opts,
          onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomFolders] })
            opts?.onSuccess?.(...args)
          },
          onError: (error, ...args) => {
            if (opts?.onError) {
              opts.onError(error, ...args)
            } else {
              handleApiError(error)
            }
          },
        })
      },
    },

    /** Tenant-scoped storage check; usable before the project exists. */
    validateStorage: {
      useMutation: (opts?: Mutation<{ fileSizeBytes: number }, void>) => {
        const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

        return useMutation({
          mutationFn: async ({ fileSizeBytes }: { fileSizeBytes: number }) => {
            await client.post(`/tenants/${currentTenantId}/data-room:validate-storage`, { fileSizeBytes })
          },
          ...opts,
          onError: (error, ...args) => {
            if (opts?.onError) {
              opts.onError(error, ...args)
            } else {
              handleApiError(error)
            }
          },
        })
      },
    },

    moveFiles: {
      useMutation: (opts?: Mutation<{ projectId: string; documentIds: string[]; folderPath?: string }, void>) => {
        const currentTenantId = useTenantStore((state) => state.currentTenant?.id)
        const queryClient = useQueryClient()

        return useMutation({
          mutationFn: async ({
            projectId,
            documentIds,
            folderPath,
          }: {
            projectId: string
            documentIds: string[]
            folderPath?: string
          }) => {
            const currentUser = queryClient.getQueryData<CurrentUser>(['user.currentUser'])
            if (!currentUser || !currentTenantId) {
              throw new Error('User not authenticated')
            }
            await client.patch(`/tenants/${currentTenantId}/projects/${projectId}/data-room/files:move`, {
              documentIds,
              folderPath,
            })
            await loadKnowledgeBase({ userId: currentUser.id, tenantId: currentTenantId, projectId })
          },
          ...opts,
          onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomDocuments] })
            queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomFolders] })
            opts?.onSuccess?.(...args)
          },
          onError: (error, ...args) => {
            if (opts?.onError) {
              opts.onError(error, ...args)
            } else {
              handleApiError(error)
            }
          },
        })
      },
    },

    upload: {
      useMutation: (opts?: Mutation<DataRoomUploadVariables, DataRoomUploadResult>) => {
        const currentTenantId = useTenantStore((state) => state.currentTenant?.id)
        const queryClient = useQueryClient()
        const { data: currentUser } = useQuery<CurrentUser>({
          queryKey: ['user.currentUser'],
          queryFn: async () => (await client.get<CurrentUser>('/users/myself')).data,
        })

        return useMutation({
          mutationFn: async ({
            projectId,
            uploads,
            folderPaths = [],
            onProgress,
          }: DataRoomUploadVariables): Promise<DataRoomUploadResult> => {
            if (!currentUser || !currentTenantId) {
              throw new Error('User not authenticated')
            }

            // The only step allowed to throw, so a 400 reaching onError always means the storage limit
            const totalBytes = uploads.reduce((sum, { file }) => sum + file.size, 0)
            await client.post(`/tenants/${currentTenantId}/data-room:validate-storage`, {
              fileSizeBytes: totalBytes,
            })

            const failedFolders = await createDataRoomFolderLevels(currentTenantId, projectId, folderPaths)
            if (folderPaths.length) queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomFolders] })

            const isUnreachable = ({ folderPath }: DataRoomUpload) => isUnderFailedFolder(folderPath, failedFolders)
            const unreachable = uploads.filter(isUnreachable)
            let done = unreachable.length
            onProgress?.(done)
            const { uploaded, alreadyExisting, failed } = await uploadFilesToDataRoomFolder(
              currentTenantId,
              projectId,
              uploads.filter((upload) => !isUnreachable(upload)),
              () => onProgress?.(++done),
            )

            const indexingFailed =
              uploaded.length > 0 &&
              (await loadKnowledgeBase({ userId: currentUser.id, tenantId: currentTenantId, projectId }).then(
                () => false,
                () => true,
              ))

            return {
              uploaded: uploaded.length,
              failed: [...unreachable.map(({ file }) => file), ...failed.map(({ file }) => file)].map(fileDisplayPath),
              alreadyExisting: alreadyExisting.map(({ file }) => fileDisplayPath(file)),
              indexingFailed,
            }
          },
          ...opts,
          // Awaited so the pending cards give way only once the refreshed list is in
          onSuccess: async (...args) => {
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomDocuments] }),
              queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomFolders] }),
              queryClient.invalidateQueries({ queryKey: [projectRouterKeys.dataRoomStorageUsage] }),
            ])
            opts?.onSuccess?.(...args)
          },
          onError: (error, ...args) => {
            if (opts?.onError) {
              opts.onError(error, ...args)
            } else {
              handleApiError(error)
            }
          },
        })
      },
    },
  },
}
